'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { use, useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import { useMediaQuery } from 'usehooks-ts';
import ViverseButton from '@/components/ViverseButton';
import ViverseSkeleton from '@/components/ViverseSkeleton';
import ViverseWorldCard from '@/components/ViverseWorldCard';
import { ViverseWorldCardSkeleton } from '@/components/ViverseWorldCardSkeleton';
import { OWNER_RECOMMEND_WORLDS_PAGE_SIZE } from '@/constants/recommend';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { withRecommendedImagePage } from '@/lib/recommended-image';
import { cn } from '@/lib/utils';
import { getCardType } from '@/lib/viverse-world-card';
import { getPublicRoomsV3, RoomDetail, type PaginatedResponse } from '@/services/cms';
const ownerRecommendWorldsClassName = 'grid w-full grid-cols-2 gap-4 md:grid-cols-6';
const ownerRecommendWorldCardClassName = 'min-w-[152px] md:min-w-0';
type OwnerRecommendWorldsProps = {
  ownerAccountId: string;
  ownerRecommendWorldsPromise: Promise<PaginatedResponse<RoomDetail> | undefined>;
};
const getOwnerRecommendWorldsKey = (ownerAccountId: string, pageIndex: number, previousPageData?: PaginatedResponse<RoomDetail>) => {
  if (pageIndex === 0) return `ownerRecommendWorlds?owner=${ownerAccountId}&start=0`;
  const next = previousPageData?.pages?.next;
  if (!next) return null;
  const query = next.split('?')[1];
  const start = new URLSearchParams(query).get('start');
  return `ownerRecommendWorlds?owner=${ownerAccountId}&start=${start || '0'}`;
};
const fetchOwnerRecommendWorlds = (key: string) => {
  const query = key.split('?')[1];
  const searchParams = new URLSearchParams(query);
  const ownerAccountId = searchParams.get('owner');
  const start = Number(searchParams.get('start') || 0);
  if (!ownerAccountId) return undefined;
  const params = {
    account_id: ownerAccountId,
    limit: OWNER_RECOMMEND_WORLDS_PAGE_SIZE,
    sort: 'create_date',
    start
  } as const;
  return getPublicRoomsV3(params).then(response => withRecommendedImagePage(response?.results));
};
export default function OwnerRecommendWorlds(t0) {
  const $ = _c(36);
  const {
    ownerAccountId,
    ownerRecommendWorldsPromise
  } = t0;
  const t = useTranslations("owner-recommend-worlds");
  const {
    roomData
  } = useCheckEnterRoomContext();
  const initialOwnerRecommendWorlds = use(ownerRecommendWorldsPromise);
  let t1;
  if ($[0] !== ownerAccountId) {
    t1 = (pageIndex, previousPageData) => getOwnerRecommendWorldsKey(ownerAccountId, pageIndex, previousPageData);
    $[0] = ownerAccountId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== initialOwnerRecommendWorlds) {
    t2 = initialOwnerRecommendWorlds ? [initialOwnerRecommendWorlds] : undefined;
    $[2] = initialOwnerRecommendWorlds;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = {
      fallbackData: t2,
      revalidateFirstPage: false
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data,
    isLoading,
    isValidating,
    size,
    setSize
  } = useSWRInfinite(t1, fetchOwnerRecommendWorlds, t3);
  const isMd = useMediaQuery("(min-width: 768px)");
  const isLg = useMediaQuery("(min-width: 1024px)");
  let t4;
  if ($[6] !== data) {
    const seenIds = new Set();
    t4 = data?.flatMap(_temp).filter(world => {
      if (!world || seenIds.has(world.id)) {
        return false;
      }
      seenIds.add(world.id);
      return true;
    }) || [];
    $[6] = data;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const ownerRecommendWorlds = t4;
  if (!ownerRecommendWorlds || ownerRecommendWorlds.length === 0) {
    return null;
  }
  const ownerDisplayName = roomData.owner.display_name;
  const canLoadMore = !!data?.[data.length - 1]?.pages?.next;
  const isLoadingMore = (isLoading || isValidating) && size > 1;
  const cols = isMd ? 6 : 2;
  const skeletonCount = (cols - ownerRecommendWorlds.length % cols) % cols || OWNER_RECOMMEND_WORLDS_PAGE_SIZE;
  let t5;
  if ($[8] !== ownerDisplayName || $[9] !== t) {
    t5 = ownerDisplayName && <h2 className="w-full overflow-hidden text-[18px] leading-8 font-semibold tracking-[0.2px] text-ellipsis text-white">{t("more-from", {
        owner: ownerDisplayName
      })}</h2>;
    $[8] = ownerDisplayName;
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = isLoadingMore && "cursor-wait";
  let t7;
  if ($[11] !== t6) {
    t7 = cn(ownerRecommendWorldsClassName, t6);
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== isLg || $[14] !== ownerRecommendWorlds) {
    let t9;
    if ($[16] !== isLg) {
      t9 = world_0 => <ViverseWorldCard key={world_0.id} info={world_0} cardOnly={!isLg} className={ownerRecommendWorldCardClassName} cardType={getCardType(world_0.content_type)} />;
      $[16] = isLg;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    t8 = ownerRecommendWorlds.map(t9);
    $[13] = isLg;
    $[14] = ownerRecommendWorlds;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[18] !== isLg || $[19] !== isLoadingMore || $[20] !== skeletonCount) {
    t9 = isLoadingMore && Array.from({
      length: skeletonCount
    }).map((_, index) => <ViverseWorldCardSkeleton key={`skeleton-${index}`} cardOnly={!isLg} className={ownerRecommendWorldCardClassName} />);
    $[18] = isLg;
    $[19] = isLoadingMore;
    $[20] = skeletonCount;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t10 = <div className={t7}>{t8}{t9}</div>;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== canLoadMore || $[27] !== isLoadingMore || $[28] !== setSize || $[29] !== size || $[30] !== t) {
    t11 = canLoadMore && <ViverseButton variant="custom" size="custom" className="flex h-9 w-full max-w-[400px] min-w-20 items-center justify-center gap-1 rounded-full bg-white/10 px-4 py-[7px] text-[#D5E2F2] transition-colors hover:bg-white/15 active:bg-white/10 disabled:bg-white/10 disabled:text-[#D5E2F2] md:h-12 md:py-[13px]" disabled={isLoadingMore} onClick={() => setSize(size + 1)}><span className="text-center text-[14px] leading-[22px] font-medium tracking-[0.15px] whitespace-nowrap">{t("load-more-games")}</span></ViverseButton>;
    $[26] = canLoadMore;
    $[27] = isLoadingMore;
    $[28] = setSize;
    $[29] = size;
    $[30] = t;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t5) {
    t12 = <section data-section="owner-recommend-worlds" className="flex w-full flex-col items-center gap-4 max-md:px-4">{t5}{t10}{t11}</section>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t5;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  return t12;
}
function _temp(page) {
  return page?.results || [];
}
export function OwnerRecommendWorldsSkeleton() {
  const $ = _c(4);
  const isLg = useMediaQuery("(min-width: 1024px)");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ViverseSkeleton className="h-8 w-full rounded-lg" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Array.from({
      length: OWNER_RECOMMEND_WORLDS_PAGE_SIZE
    });
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isLg) {
    t2 = <section data-section="owner-recommend-worlds" className="flex w-full flex-col items-center gap-4 pt-2 pb-4 max-md:px-4">{t0}<div className={ownerRecommendWorldsClassName}>{t1.map((_, index) => <ViverseWorldCardSkeleton key={index} cardOnly={!isLg} className={ownerRecommendWorldCardClassName} />)}</div></section>;
    $[2] = isLg;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}